import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as faceapi from "face-api.js";
const API_BASE = "https://schoolcrm-fzcv.onrender.com/api";
const MATCH_THRESHOLD = 0.5;
const SCAN_INTERVAL_MS = 1500;
const MARK_COOLDOWN_MS = 8000;

// ── UPDATED COLORS TO MATCH YOUR REQUEST ──
const STATUS_STYLES = {
  Present: {
    bg: "#2C5EAD",
    color: "#FFFFFF",
    dot: "#FFFFFF"
  },
  Late: {
    bg: "#C4E2F5",
    color: "#2C5EAD",
    dot: "#1591DC"
  },
  Absent: {
    bg: "#C4E2F5",
    color: "#2C5EAD",
    dot: "#1591DC"
  }
};
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function initials(name) {
  if (!name) return "?";
  return name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
}
function AttStatusBadge({
  status
}) {
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.Absent;
  return <span style={{
    background: cfg.bg,
    color: cfg.color,
    padding: "4px 12px",
    borderRadius: 24,
    fontSize: 11,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    boxShadow: cfg.bg === "#C4E2F5" ? "0 1px 3px rgba(44,94,173,0.1)" : "none"
  }}>
      <span style={{
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: cfg.dot
    }} />
      {status}
    </span>;
}
export default function WorkerAttendance() {
  const navigate = useNavigate();
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [modelError, setModelError] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [facesRegistered, setFacesRegistered] = useState([]);
  const [matcher, setMatcher] = useState(null);
  const [dayRecords, setDayRecords] = useState([]);
  const [date, setDate] = useState(todayStr());
  const [loadingTable, setLoadingTable] = useState(true);
  const [view, setView] = useState("daily");
  const [summary, setSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryFrom, setSummaryFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [summaryTo, setSummaryTo] = useState(todayStr());
  const [tab, setTab] = useState("scan");
  const [cameraOn, setCameraOn] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [toast, setToast] = useState(null);
  const [registerWorkerId, setRegisterWorkerId] = useState("");
  const [registering, setRegistering] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanTimerRef = useRef(null);
  const lastMarkedRef = useRef({});

  // ── Load face-api models ──
  useEffect(() => {
    (async () => {
      try {
        const MODEL_URL = "/models";
        await Promise.all([faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL), faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL), faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)]);
        setModelsLoaded(true);
      } catch (err) {
        console.error("Model load error:", err);
        setModelError("Could not load face-recognition models from /models. Make sure the weight files are in your public/models folder.");
      }
    })();
  }, []);

  // ── Fetch workers, registered faces, and today's attendance ──
  const fetchWorkers = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/workers`);
      const data = await r.json();
      setWorkers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchWorkers error:", err);
    }
  }, []);
  const fetchFaces = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/worker-attendance/faces`);
      const data = await r.json();
      setFacesRegistered(Array.isArray(data) ? data : []);
      const labeled = (Array.isArray(data) ? data : []).filter(w => Array.isArray(w.faceDescriptor) && w.faceDescriptor.length === 128).map(w => new faceapi.LabeledFaceDescriptors(String(w._id), [new Float32Array(w.faceDescriptor)]));
      setMatcher(labeled.length ? new faceapi.FaceMatcher(labeled, MATCH_THRESHOLD) : null);
    } catch (err) {
      console.error("fetchFaces error:", err);
    }
  }, []);
  const fetchDay = useCallback(async d => {
    setLoadingTable(true);
    try {
      const r = await fetch(`${API_BASE}/worker-attendance?date=${d}`);
      const data = await r.json();
      setDayRecords(data.records || []);
    } catch (err) {
      console.error("fetchDay error:", err);
      setDayRecords([]);
    } finally {
      setLoadingTable(false);
    }
  }, []);
  const fetchSummary = useCallback(async (from, to) => {
    setSummaryLoading(true);
    try {
      const r = await fetch(`${API_BASE}/worker-attendance/summary?from=${from}&to=${to}`);
      const data = await r.json();
      setSummary(data.summary || []);
    } catch (err) {
      console.error("fetchSummary error:", err);
      setSummary([]);
    } finally {
      setSummaryLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchWorkers();
    fetchFaces();
  }, [fetchWorkers, fetchFaces]);
  useEffect(() => {
    fetchDay(date);
  }, [date, fetchDay]);
  useEffect(() => {
    if (view === "summary") fetchSummary(summaryFrom, summaryTo);
  }, [view, summaryFrom, summaryTo, fetchSummary]);

  // ── Camera control ──
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: 480,
          height: 360
        }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOn(true);
    } catch (err) {
      console.error("Camera error:", err);
      setToast({
        type: "error",
        text: "Could not access camera. Check permissions."
      });
    }
  }, []);
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
    setScanning(false);
    if (scanTimerRef.current) {
      clearInterval(scanTimerRef.current);
      scanTimerRef.current = null;
    }
  }, []);
  useEffect(() => stopCamera, [stopCamera]);

  // ── Live scan loop ──
  const runScanTick = useCallback(async () => {
    if (!videoRef.current || !matcher || !modelsLoaded) return;
    try {
      const detection = await faceapi.detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions()).withFaceLandmarks().withFaceDescriptor();
      if (!detection) return;
      const best = matcher.findBestMatch(detection.descriptor);
      if (best.label === "unknown") {
        setToast({
          type: "warn",
          text: "Face not recognized. Please register first."
        });
        return;
      }
      const workerId = best.label;
      const now = Date.now();
      const last = lastMarkedRef.current[workerId] || 0;
      if (now - last < MARK_COOLDOWN_MS) return;
      lastMarkedRef.current[workerId] = now;
      const r = await fetch(`${API_BASE}/worker-attendance/mark`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          workerId,
          method: "Face"
        })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Failed to mark attendance");
      setToast({
        type: "success",
        text: data.message
      });
      fetchDay(date);
    } catch (err) {
      console.error("scan tick error:", err);
    }
  }, [matcher, modelsLoaded, date, fetchDay]);
  const startScanning = async () => {
    if (!cameraOn) await startCamera();
    setScanning(true);
  };
  useEffect(() => {
    if (scanning) {
      scanTimerRef.current = setInterval(runScanTick, SCAN_INTERVAL_MS);
    } else if (scanTimerRef.current) {
      clearInterval(scanTimerRef.current);
      scanTimerRef.current = null;
    }
    return () => {
      if (scanTimerRef.current) clearInterval(scanTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning, runScanTick]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Register face for a worker ──
  const handleCaptureFace = async () => {
    if (!registerWorkerId) {
      setToast({
        type: "warn",
        text: "Select a worker first."
      });
      return;
    }
    if (!cameraOn) await startCamera();
    setRegistering(true);
    try {
      const detection = await faceapi.detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions()).withFaceLandmarks().withFaceDescriptor();
      if (!detection) {
        setToast({
          type: "error",
          text: "No face detected. Face the camera clearly and try again."
        });
        return;
      }
      const r = await fetch(`${API_BASE}/worker-attendance/register-face/${registerWorkerId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          descriptor: Array.from(detection.descriptor)
        })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Failed to register face");
      setToast({
        type: "success",
        text: "Face registered successfully!"
      });
      fetchFaces();
    } catch (err) {
      console.error("register face error:", err);
      setToast({
        type: "error",
        text: err.message
      });
    } finally {
      setRegistering(false);
    }
  };

  // ── Logout ──
  const handleLogout = () => {
    stopCamera();
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("username");
    navigate("/supervoicerlogin");
  };
  const registeredIds = new Set(facesRegistered.map(w => String(w._id)));
  return <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css');
        *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #C4E2F5; font-family: 'Inter', sans-serif; }
        .att-tab { transition: all 0.2s ease; cursor: pointer; }
        .att-btn { transition: all 0.2s ease; cursor: pointer; }
        .att-btn:hover { transform: translateY(-1px); filter: brightness(0.96); }
        .att-row:hover { background: #E3F2FD !important; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #C4E2F5; }
        ::-webkit-scrollbar-thumb { background: #2C5EAD; border-radius: 6px; }
      `}</style>

      <div style={{
      minHeight: "100vh",
      background: "#C4E2F5",
      fontFamily: "'Inter', sans-serif"
    }}>
        {/* HEADER - UPDATED TO #2C5EAD */}
        <div style={{
        background: "#2C5EAD",
        padding: "14px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 100,
        boxShadow: "0 4px 20px rgba(44,94,173,0.15)",
        flexWrap: "wrap",
        gap: 12
      }}>
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 12
        }}>
            <div style={{
            width: 40,
            height: 40,
            borderRadius: 14,
            background: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            boxShadow: "0 4px 12px rgba(44,94,173,0.3)"
          }}>
              <i className="fas fa-user-check" style={{
              color: "#2C5EAD"
            }}></i>
            </div>
            <div>
              <div style={{
              fontWeight: 800,
              fontSize: 16,
              color: "#FFFFFF"
            }}>
                Worker Attendance
              </div>
              <div style={{
              fontSize: 9,
              color: "#C4E2F5",
              letterSpacing: "0.6px",
              textTransform: "uppercase",
              fontWeight: 600
            }}>
                Face Recognition · 9:00 – 9:45 AM window
              </div>
            </div>
          </div>
          <div style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap"
        }}>
            <button onClick={() => setView(view === "summary" ? "daily" : "summary")} className="att-btn" style={{
            background: view === "summary" ? "#2C5EAD" : "#FFFFFF",
            color: view === "summary" ? "#FFFFFF" : "#2C5EAD",
            padding: "6px 14px",
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            boxShadow: view === "summary" ? "0 2px 8px rgba(44,94,173,0.2)" : "0 1px 3px rgba(0,0,0,0.05)"
          }}>
              <i className="fas fa-chart-bar" style={{
              marginRight: 6
            }}></i>
              {view === "summary" ? "Daily View" : "Attendance Summary"}
            </button>
            <button onClick={handleLogout} className="att-btn" style={{
            background: "#FFFFFF",
            color: "#2C5EAD",
            padding: "6px 14px",
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
              <i className="fas fa-arrow-right-from-bracket" style={{
              marginRight: 6
            }}></i>Logout
            </button>
          </div>
        </div>

        {modelError && <div style={{
        margin: "16px 24px",
        background: "#FFFFFF",
        color: "#2C5EAD",
        padding: "12px 16px",
        borderRadius: 12,
        fontSize: 13,
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
      }}>
            <i className="fas fa-exclamation-circle" style={{
          marginRight: 8
        }}></i>{modelError}
          </div>}

        {/* TOAST */}
        {toast && <div style={{
        position: "fixed",
        top: 76,
        right: 24,
        zIndex: 2000,
        animation: "fadeUp 0.3s ease",
        background: toast.type === "success" ? "#2C5EAD" : toast.type === "warn" ? "#C4E2F5" : "#C4E2F5",
        color: toast.type === "success" ? "#FFFFFF" : toast.type === "warn" ? "#2C5EAD" : "#2C5EAD",
        padding: "12px 18px",
        borderRadius: 14,
        fontSize: 13,
        fontWeight: 600,
        boxShadow: "0 8px 24px rgba(44,94,173,0.1)",
        maxWidth: 320
      }}>
            {toast.text}
          </div>}

        {view === "summary" ? <div style={{
        padding: "20px 24px 40px"
      }}>
            <div style={{
          background: "#FFFFFF",
          borderRadius: 24,
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(44,94,173,0.04)"
        }}>
              <div style={{
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            background: "#C4E2F5"
          }}>
                <div style={{
              fontWeight: 800,
              fontSize: 14,
              color: "#2C5EAD"
            }}>
                  Attendance Summary <span style={{
                fontWeight: 400,
                fontSize: 11,
                color: "#1591DC"
              }}>({summary.length} workers)</span>
                </div>
                <div style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap"
            }}>
                  <span style={{
                fontSize: 11,
                color: "#1591DC"
              }}>From</span>
                  <input type="date" value={summaryFrom} onChange={e => setSummaryFrom(e.target.value)} style={{
                borderRadius: 10,
                padding: "6px 12px",
                fontSize: 12,
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
              }} />
                  <span style={{
                fontSize: 11,
                color: "#1591DC"
              }}>To</span>
                  <input type="date" value={summaryTo} onChange={e => setSummaryTo(e.target.value)} style={{
                borderRadius: 10,
                padding: "6px 12px",
                fontSize: 12,
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
              }} />
                </div>
              </div>

              <div style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr",
            gap: 10,
            padding: "10px 20px",
            background: "#2C5EAD"
          }}>
                {["Worker", "Role", "Present", "Late", "Leave / Absent", "Total Days"].map(h => <div key={h} style={{
              fontSize: 9,
              fontWeight: 700,
              color: "#FFFFFF",
              textTransform: "uppercase",
              letterSpacing: "1px"
            }}>{h}</div>)}
              </div>

              {summaryLoading ? <div style={{
            padding: 40,
            textAlign: "center",
            color: "#1591DC",
            fontSize: 13
          }}>
                  <i className="fas fa-spinner fa-pulse" style={{
              marginRight: 8
            }}></i>Loading...
                </div> : summary.length === 0 ? <div style={{
            padding: 40,
            textAlign: "center",
            color: "#1591DC",
            fontSize: 13
          }}>No data for this range</div> : summary.map((w, idx) => <div key={w.workerId} className="att-row" style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr",
            gap: 10,
            padding: "12px 20px",
            alignItems: "center",
            background: idx % 2 === 0 ? "#FFFFFF" : "#C4E2F5"
          }}>
                    <div style={{
              display: "flex",
              alignItems: "center",
              gap: 10
            }}>
                      <div style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: "#2C5EAD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                color: "#FFFFFF"
              }}>
                        {initials(w.name)}
                      </div>
                      <span style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#2C5EAD"
              }}>{w.name}</span>
                    </div>
                    <div style={{
              fontSize: 11,
              color: "#1591DC"
            }}>{w.role}</div>
                    <div style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#2C5EAD"
            }}>{w.presentCount}</div>
                    <div style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#2C5EAD"
            }}>{w.lateCount}</div>
                    <div style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#2C5EAD"
            }}>{w.leaveCount}</div>
                    <div style={{
              fontSize: 11,
              color: "#1591DC"
            }}>{w.totalDays}</div>
                  </div>)}
            </div>
            <div style={{
          marginTop: 12,
          fontSize: 11,
          color: "#1591DC"
        }}>
              <i className="fas fa-info-circle" style={{
            marginRight: 6
          }}></i>
              "Leave / Absent" = calendar days in the selected range with no face check-in recorded for that worker.
            </div>
          </div> : <div style={{
        padding: "20px 24px 40px",
        display: "grid",
        gridTemplateColumns: "minmax(320px, 420px) 1fr",
        gap: 20
      }}>
          {/* LEFT: CAMERA PANEL */}
          <div style={{
          background: "#FFFFFF",
          borderRadius: 24,
          padding: 20,
          boxShadow: "0 2px 12px rgba(44,94,173,0.04)",
          alignSelf: "start"
        }}>
            <div style={{
            display: "flex",
            gap: 8,
            marginBottom: 16
          }}>
              {["scan", "register"].map(t => <button key={t} className="att-tab" onClick={() => {
              setTab(t);
              setScanning(false);
            }} style={{
              flex: 1,
              padding: "8px 12px",
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 700,
              background: tab === t ? "#2C5EAD" : "#C4E2F5",
              color: tab === t ? "#FFFFFF" : "#2C5EAD",
              boxShadow: tab === t ? "0 2px 8px rgba(44,94,173,0.2)" : "0 1px 3px rgba(0,0,0,0.05)"
            }}>
                  {t === "scan" ? "Scan & Mark" : "Register Face"}
                </button>)}
            </div>

            <div style={{
            position: "relative",
            background: "#2C5EAD",
            borderRadius: 16,
            overflow: "hidden",
            aspectRatio: "4/3"
          }}>
              <video ref={videoRef} muted playsInline style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform: "scaleX(-1)"
            }} />
              {!cameraOn && <div style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#C4E2F5",
              fontSize: 13
            }}>
                  <i className="fas fa-video-slash" style={{
                marginRight: 8
              }}></i>Camera off
                </div>}
              {!modelsLoaded && !modelError && <div style={{
              position: "absolute",
              inset: 0,
              background: "rgba(44,94,173,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFFFFF",
              fontSize: 12
            }}>
                  <i className="fas fa-spinner fa-pulse" style={{
                marginRight: 8
              }}></i>Loading face models...
                </div>}
            </div>

            {tab === "scan" ? <div style={{
            marginTop: 16,
            display: "flex",
            gap: 10
          }}>
                {!scanning ? <button className="att-btn" onClick={startScanning} disabled={!modelsLoaded || !matcher} style={{
              flex: 1,
              background: !modelsLoaded || !matcher ? "#4BB8FA" : "#2C5EAD",
              color: "#FFFFFF",
              border: "none",
              padding: 12,
              borderRadius: 14,
              fontSize: 13,
              fontWeight: 800,
              cursor: !modelsLoaded || !matcher ? "not-allowed" : "pointer",
              boxShadow: !modelsLoaded || !matcher ? "none" : "0 2px 8px rgba(44,94,173,0.2)"
            }}>
                    <i className="fas fa-play" style={{
                marginRight: 8
              }}></i>
                    {matcher ? "Start Scanning" : "No faces registered yet"}
                  </button> : <button className="att-btn" onClick={() => {
              setScanning(false);
              stopCamera();
            }} style={{
              flex: 1,
              background: "#C4E2F5",
              color: "#2C5EAD",
              border: "none",
              padding: 12,
              borderRadius: 14,
              fontSize: 13,
              fontWeight: 800
            }}>
                    <i className="fas fa-stop" style={{
                marginRight: 8
              }}></i>Stop Scanning
                  </button>}
              </div> : <div style={{
            marginTop: 16
          }}>
                <label style={{
              fontSize: 11,
              color: "#2C5EAD",
              fontWeight: 600,
              display: "block",
              marginBottom: 6,
              textTransform: "uppercase",
              letterSpacing: "0.5px"
            }}>
                  Select Worker
                </label>
                <select value={registerWorkerId} onChange={e => setRegisterWorkerId(e.target.value)} style={{
              width: "100%",
              background: "#FFFFFF",
              borderRadius: 12,
              padding: "10px 14px",
              fontSize: 13,
              marginBottom: 12,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
                  <option value="">Choose a worker...</option>
                  {workers.map(w => <option key={w._id} value={w._id}>
                      {w.name} {registeredIds.has(String(w._id)) ? "(already registered)" : ""}
                    </option>)}
                </select>
                <div style={{
              display: "flex",
              gap: 10
            }}>
                  {!cameraOn && <button className="att-btn" onClick={startCamera} style={{
                flex: 1,
                background: "#C4E2F5",
                color: "#2C5EAD",
                padding: 12,
                borderRadius: 14,
                fontSize: 13,
                fontWeight: 700,
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
              }}>
                      <i className="fas fa-video" style={{
                  marginRight: 8
                }}></i>Start Camera
                    </button>}
                  <button className="att-btn" onClick={handleCaptureFace} disabled={!modelsLoaded || registering || !registerWorkerId} style={{
                flex: 1,
                background: !modelsLoaded || registering || !registerWorkerId ? "#4BB8FA" : "#2C5EAD",
                color: "#FFFFFF",
                border: "none",
                padding: 12,
                borderRadius: 14,
                fontSize: 13,
                fontWeight: 800,
                cursor: !modelsLoaded || registering || !registerWorkerId ? "not-allowed" : "pointer",
                boxShadow: !modelsLoaded || registering || !registerWorkerId ? "none" : "0 2px 8px rgba(44,94,173,0.2)"
              }}>
                    {registering ? "Capturing..." : <><i className="fas fa-camera" style={{
                    marginRight: 8
                  }}></i>Capture Face</>}
                  </button>
                </div>
                <div style={{
              fontSize: 11,
              color: "#1591DC",
              marginTop: 10,
              lineHeight: 1.6
            }}>
                  Look straight at the camera in good lighting, then capture. This face will be used to mark attendance automatically.
                </div>
              </div>}

            <div style={{
            marginTop: 16,
            fontSize: 11,
            color: "#1591DC",
            background: "#C4E2F5",
            padding: "10px 14px",
            borderRadius: 12,
            lineHeight: 1.7
          }}>
              <i className="fas fa-info-circle" style={{
              marginRight: 6
            }}></i>
              {facesRegistered.length} of {workers.length} workers have a registered face.
              Attendance marked between <b style={{
              color: "#2C5EAD"
            }}>9:00–9:45 AM</b> is <b style={{
              color: "#2C5EAD"
            }}>Present</b>; after that it's <b style={{
              color: "#2C5EAD"
            }}>Late</b>, whenever the worker's face is recognized.
            </div>
          </div>

          {/* RIGHT: DAILY TABLE */}
          <div style={{
          background: "#FFFFFF",
          borderRadius: 24,
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(44,94,173,0.04)"
        }}>
            <div style={{
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            background: "#C4E2F5"
          }}>
              <div style={{
              fontWeight: 800,
              fontSize: 14,
              color: "#2C5EAD"
            }}>
                Attendance <span style={{
                fontWeight: 400,
                fontSize: 11,
                color: "#1591DC"
              }}>({dayRecords.length} workers)</span>
              </div>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{
              borderRadius: 10,
              padding: "6px 12px",
              fontSize: 12,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }} />
            </div>

            <div style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr",
            gap: 10,
            padding: "10px 20px",
            background: "#2C5EAD"
          }}>
              {["Worker", "Role", "Check-in", "Method", "Status"].map(h => <div key={h} style={{
              fontSize: 9,
              fontWeight: 700,
              color: "#FFFFFF",
              textTransform: "uppercase",
              letterSpacing: "1px"
            }}>{h}</div>)}
            </div>

            {loadingTable ? <div style={{
            padding: 40,
            textAlign: "center",
            color: "#1591DC",
            fontSize: 13
          }}>
                <i className="fas fa-spinner fa-pulse" style={{
              marginRight: 8
            }}></i>Loading...
              </div> : dayRecords.length === 0 ? <div style={{
            padding: 40,
            textAlign: "center",
            color: "#1591DC",
            fontSize: 13
          }}>No workers found</div> : dayRecords.map((rec, idx) => <div key={rec.workerId} className="att-row" style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr",
            gap: 10,
            padding: "12px 20px",
            alignItems: "center",
            background: idx % 2 === 0 ? "#FFFFFF" : "#C4E2F5"
          }}>
                  <div style={{
              display: "flex",
              alignItems: "center",
              gap: 10
            }}>
                    <div style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: "#2C5EAD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                color: "#FFFFFF"
              }}>
                      {initials(rec.name)}
                    </div>
                    <span style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#2C5EAD"
              }}>{rec.name}</span>
                  </div>
                  <div style={{
              fontSize: 11,
              color: "#1591DC"
            }}>{rec.role}</div>
                  <div style={{
              fontSize: 11,
              color: "#1591DC"
            }}>{rec.checkInTime || "—"}</div>
                  <div style={{
              fontSize: 11,
              color: "#1591DC"
            }}>{rec.method || "—"}</div>
                  <div><AttStatusBadge status={rec.attendanceStatus} /></div>
                </div>)}
          </div>
        </div>}
      </div>
    </>;
}